import { useEffect, useRef, useState } from "react";

import { requestCartSync } from "@repo/shopifyV2/api/requestCartSync";
import {
  cartBuyerIdentityUpdateMutation,
  cartCreateInputMutation,
  cartLinesAddMutation,
  cartLinesUpdateMutation,
  cartQuery,
} from "@repo/shopifyV2/graphql/cart-gql";
import { useGraphQLClient } from "@repo/shopifyV2/graphql/client";
import { caaCustomerCartIdQuery } from "@repo/shopifyV2/graphql/customer-account-gql";
import { customerCartIdQuery } from "@repo/shopifyV2/graphql/customer-gql";
import { GIFT_TAGS } from "@repo/shopifyV2/hooks/useGift";
import type {
  ICart,
  ICartBuyerIdentityUpdateMutation,
  ICartCreateInputMutation,
  ICartEdge,
  ICartLinesAddMutation,
  ICartLinesUpdateMutation,
} from "@repo/shopifyV2/interface/cart";
import type { ICustomerCartIdQuery, ICustomerQuery, IOrdersEdgesNode } from "@repo/shopifyV2/interface/customer";
import type { ICaaCustomer, ICaaCustomerCartIdQuery } from "@repo/shopifyV2/interface/customer-account";
import { extractId } from "@repo/shopifyV2/utils/common";

import useLanguage from "@hooks/useLanguage";

import { caaRequest, getFreshCaaToken } from "@lib/caaClient";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";

type TCart = ICart["cart"];

interface ICartLineInput {
  merchandiseId: string;
  quantity: number;
}

interface ICartLineUpdateInput {
  id: string;
  quantity: number;
}

type TOwnership = "guest" | "mine" | "other";

interface IClampState {
  /** merchandiseId별 원격 카트에 담긴 수량 + 지금까지 옮기기로 한 수량 */
  variantQty: Record<string, number>;
  /** productId별 위와 동일 */
  productQty: Record<string, number>;
  lines: ICartLineInput[];
}

/** 지난 주문에서 구매한 수량 (패밀리세일 제외, 취소/환불 반영) */
interface IOrderedQty {
  /** merchandiseId(variantId)별 */
  variantQty: Record<string, number>;
  /** productId별 */
  productQty: Record<string, number>;
}

const isGiftLine = ({ merchandise }: ICartEdge["node"]) => merchandise.product.tags.some((tag) => GIFT_TAGS.has(tag));

/** 카트 라인의 수량을 key별로 합산 (gift 라인은 useGift가 다시 계산하므로 제외) */
const sumQuantityBy = (cart: TCart, getKey: (node: ICartEdge["node"]) => string) =>
  (cart.lines.edges as ICartEdge[]).reduce<Record<string, number>>((acc, { node }) => {
    if (isGiftLine(node)) return acc;
    const key = getKey(node);
    return { ...acc, [key]: (acc[key] ?? 0) + node.quantity };
  }, {});

/** merchandiseId → quantity */
const toLineMap = (cart: TCart) => sumQuantityBy(cart, (node) => node.merchandise.id);

/** productId → quantity */
const toProductMap = (cart: TCart) => sumQuantityBy(cart, (node) => node.merchandise.product.id);

/** 수량 제한 체크용 지난 주문 수량 집계. checkProductLimitReached(Caa) / checkVariantLimitReached(Caa)와 동일 기준 */
const getOrderedQty = (isJp: boolean, customer: ICustomerQuery["customer"], caaCustomer: ICaaCustomer | null): IOrderedQty => {
  const lines = isJp
    ? (caaCustomer?.orders?.edges ?? [])
        .filter(({ node }) => node.isfamilysale?.value !== "true")
        .flatMap(({ node }) => node.lineItems.edges)
        .map(({ node }) => ({ variantId: node.variantId, productId: node.productId, quantity: node.refundableQuantity }))
    : customer.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
        .flatMap(({ node }) => node.lineItems.edges)
        .map(({ node }) => ({ variantId: node.variant?.id, productId: node.variant?.product.id, quantity: node.currentQuantity || 0 }));

  return lines.reduce<IOrderedQty>(
    (acc, { variantId, productId, quantity }) => ({
      variantQty: variantId ? { ...acc.variantQty, [variantId]: (acc.variantQty[variantId] ?? 0) + quantity } : acc.variantQty,
      productQty: productId ? { ...acc.productQty, [productId]: (acc.productQty[productId] ?? 0) + quantity } : acc.productQty,
    }),
    { variantQty: {}, productQty: {} },
  );
};

/** 카트의 소유 상태: guest(buyerIdentity 없음 - 비로그인 상태) / mine(현재 로그인한 나) / other(로그인 했던 다른 사람) */
const getOwnership = (cart: TCart, customerId: string): TOwnership => {
  const buyerId = cart.buyerIdentity?.customer?.id;
  if (!buyerId) return "guest";
  return extractId(buyerId) === extractId(customerId) ? "mine" : "other";
};

/** await 도중 로그아웃(또는 다른 계정으로 전환)했다면 사용자 카트로 덮어쓰지 않도록 중단 */
const assertStillLoggedIn = (customerId: string, isJp: boolean) => {
  const current = isJp ? useCustomerStore.getState().caaCustomer?.id : useCustomerStore.getState().customer.id;
  if (current !== customerId) throw new Error("[CART SYNC] aborted: logged out");
};

/** 원격 카트로 옮길 게스트 라인 (gift 제외). 로그아웃 시 카트를 초기화하므로 게스트 카트의 라인은 전부 이 브라우저에서 비로그인으로 담은 것 */
const getCarryLines = (local: TCart): ICartLineInput[] =>
  Object.entries(toLineMap(local)).map(([merchandiseId, quantity]) => ({ merchandiseId, quantity }));

/**
 * 재고/수량 제한(variant·product)을 넘지 않도록, 원격 카트에 이미 담긴 수량을 감안해 옮길 수량을 자름
 * 수량 제한은 지난 주문에서 구매한 수량(ordered)까지 포함해서 계산 (재고에는 영향 없음)
 */
const clampCarryLines = (lines: ICartLineInput[], local: TCart, remote: TCart | null, ordered: IOrderedQty): ICartLineInput[] => {
  // ICart["cart"]의 lines 타입은 Product 메타필드(quantityLimit 등)를 잃으므로 ICartEdge로 좁힘
  const nodeByMerchandise = new Map((local.lines.edges as ICartEdge[]).map(({ node }) => [node.merchandise.id, node]));

  const initial: IClampState = {
    variantQty: remote ? toLineMap(remote) : {},
    productQty: remote ? toProductMap(remote) : {},
    lines: [],
  };

  // 같은 상품의 앞선 라인이 남은 수량을 소진하므로 순서대로 누적
  const { lines: clamped } = lines.reduce<IClampState>((state, line) => {
    const node = nodeByMerchandise.get(line.merchandiseId);
    if (!node) return { ...state, lines: [...state.lines, line] };

    const { merchandise } = node;
    const productId = merchandise.product.id;
    const variantUsed = state.variantQty[merchandise.id] ?? 0;
    const productUsed = state.productQty[productId] ?? 0;
    const variantOrdered = ordered.variantQty[merchandise.id] ?? 0;
    const productOrdered = ordered.productQty[productId] ?? 0;

    // 재고는 falsy면 제한 없음(adjustCartLines와 동일), 제한 메타필드는 양수일 때만 적용
    const available = merchandise.quantityAvailable; // 현재 남아있는 재고 수량
    const variantLimit = parseInt(merchandise.product.quantityLimit?.value || "-1"); // Variant의 최대 구매 제한 수량
    const productLimit = parseInt(merchandise.product.quantityLimitProduct?.value || "-1"); // Product 전체 단위의 최대 구매 제한 수량

    // 세 가지 조건 중 가장 작은 한도 내로 수량을 제한
    const quantity = Math.max(
      Math.min(
        line.quantity,
        available ? available - variantUsed : Infinity,
        variantLimit > 0 ? variantLimit - variantUsed - variantOrdered : Infinity,
        productLimit > 0 ? productLimit - productUsed - productOrdered : Infinity,
      ),
      0,
    );

    return {
      variantQty: { ...state.variantQty, [merchandise.id]: variantUsed + quantity },
      productQty: { ...state.productQty, [productId]: productUsed + quantity },
      lines: [...state.lines, { ...line, quantity }],
    };
  }, initial);

  return clamped.filter((line) => line.quantity > 0);
};

/**
 * 게스트 카트 우선: 게스트 라인이 Product 구매 제한에 걸려 못 들어가면, 원격 카트에서 같은 상품의 "다른 옵션" 라인을 넘치는 만큼 줄여 자리를 만듦
 *
 * 같은 옵션끼리는 어떤 것을 남겨도 결과가 같으므로 원격을 건드리지 않고 clampCarryLines가 게스트 수량을 줄이게 함
 * 재고·variant 제한도 같은 옵션 안에서의 합산 문제라 여기서 다루지 않음
 */
const getRemoteReductions = (guestLines: ICartLineInput[], local: TCart, remote: TCart, ordered: IOrderedQty): ICartLineUpdateInput[] => {
  const nodeByMerchandise = new Map((local.lines.edges as ICartEdge[]).map(({ node }) => [node.merchandise.id, node]));
  const guestVariantIds = new Set(guestLines.map((line) => line.merchandiseId));

  // Product 제한이 걸린 상품별로 게스트가 가져올 수량을 합산
  const guestByProduct = guestLines.reduce<Record<string, { limit: number; quantity: number }>>((acc, line) => {
    const product = nodeByMerchandise.get(line.merchandiseId)?.merchandise.product;
    const limit = parseInt(product?.quantityLimitProduct?.value || "-1");
    if (!product || limit <= 0) return acc;

    return { ...acc, [product.id]: { limit, quantity: (acc[product.id]?.quantity ?? 0) + line.quantity } };
  }, {});

  // 상품별로 제한을 넘치는 수량 (지난 주문 수량 포함)
  const remoteProductQty = toProductMap(remote);
  const overflow = Object.fromEntries(
    Object.entries(guestByProduct).map(([productId, { limit, quantity }]) => [
      productId,
      Math.max(quantity + (remoteProductQty[productId] ?? 0) - (limit - (ordered.productQty[productId] ?? 0)), 0),
    ]),
  );

  const { updates } = (remote.lines.edges as ICartEdge[]).reduce<{ remaining: Record<string, number>; updates: ICartLineUpdateInput[] }>(
    (state, { node }) => {
      const productId = node.merchandise.product.id;
      const remaining = state.remaining[productId] ?? 0;
      if (!remaining || isGiftLine(node) || guestVariantIds.has(node.merchandise.id)) return state;

      const cut = Math.min(remaining, node.quantity);

      return {
        remaining: { ...state.remaining, [productId]: remaining - cut },
        updates: [...state.updates, { id: node.id, quantity: node.quantity - cut }],
      };
    },
    { remaining: overflow, updates: [] },
  );

  return updates;
};

/**
 * 로그인 사용자 기준 카트 복원/병합
 *
 * - 로그인 시(또는 로그인 상태로 부팅 시) 1회: 원격 cartId(customer metafield)를 읽어 로컬 카트와 동기화
 *   - guest 카트 + 원격 카트: 게스트 카트의 라인을 원격 카트에 add(같은 상품은 Shopify가 수량 합산) 후 원격 카트 채택
 *     Product 구매 제한으로 둘 다 담을 수 없으면 게스트 카트 우선(원격의 같은 상품 다른 옵션을 줄임)
 *   - guest 카트 + 원격 없음: 게스트 라인으로 새 카트 생성 후 buyerIdentity 연결
 *   - mine 카트: 원격이 있고 id가 다르면 원격을 진실로 보고 채택(병합 X → 이중 합산 방지)
 * - 이후 로그인한 카트의 id가 바뀔 때마다(생성/만료 재생성/주문 완료 후 리셋) 원격 cartId 갱신
 *
 * jp는 New Customer Accounts라 classic customer 기능(Storefront customer(customerAccessToken:) 쿼리, customerAccessToken 로그인)을 못 씀 → 로그인 판별/원격 cartId 조회는 Customer Account API(CAA, caaRequest)로 따로 함.
 * 다만 Cart 자체(cartCreate/cartLinesAdd/cartBuyerIdentityUpdate 등)는 로케일과 무관하게 항상 classic Storefront 스키마이고,
 * buyerIdentity.customerAccessToken 필드에 CAA access token을 그대로 넣는 것도 App.tsx의 기존 로직과 동일한 전제
 */
export default function useCartSync() {
  // Cart 조회/뮤테이션은 로케일 무관하게 항상 classic Storefront 스키마다
  const request = useGraphQLClient();
  const { lang } = useLanguage();
  const { cart, updateCart, setSyncing } = useCartStore();
  const { customer, caaCustomer, token, caaToken } = useCustomerStore();

  const isJp = lang === "jp";
  const customerId = isJp ? caaCustomer?.id : customer.id;
  const hasSession = isJp ? Boolean(caaToken?.accessToken) : Boolean(token.accessToken);

  const [syncedFor, setSyncedFor] = useState<string | null>(null);
  const runningRef = useRef(false);
  /** 원격(metafield)에 저장돼 있다고 알고 있는 cartId */
  const lastSavedRef = useRef<string | null>(null);

  /**
   * cart 뮤테이션(buyerIdentity 등)에 넣을, 만료되지 않은 customerAccessToken 값.
   * getFreshCaaToken()이 만료 임박 시 갱신까지 해서 반환하므로 매번 새로 구함(App.tsx의 기존 resetCart와 동일 전제)
   */
  const getBuyerAccessToken = async (): Promise<string | null> =>
    isJp ? ((await getFreshCaaToken().catch(() => null))?.accessToken ?? null) : token.accessToken || null;

  // 동기화 도중 customer가 갱신될 수 있어 호출 시점의 store 값으로 계산
  const getCurrentOrderedQty = () => {
    const state = useCustomerStore.getState();
    return getOrderedQty(isJp, state.customer, state.caaCustomer);
  };

  // 고객의 메타필드에 저장되어 있던 카트 ID를 조회. jp는 classic customer 쿼리가 막혀 있어 CAA로 따로 조회(인증은 caaRequest가 자체 처리)
  const fetchRemoteCartId = async (customerAccessToken: string) => {
    if (isJp) {
      const data = await caaRequest<ICaaCustomerCartIdQuery>(caaCustomerCartIdQuery);
      return data.customer?.cartId?.value || null;
    }

    const data = await request<ICustomerCartIdQuery>(customerCartIdQuery, { customerAccessToken });
    return data.customer?.cartId?.value || null;
  };

  // 카트 ID 조회 - useCart(useCartQuery)의 getCart/mutation은 응답을 바로 store에 덮어써서 병합 전 게스트 카트가 사라지므로 직접 조회한다
  const fetchCart = async (id: string): Promise<TCart | null> => {
    const data = await request<ICart>(cartQuery, { id });
    return data.cart;
  };

  // 카트에 상품을 추가
  const addLines = async (cartId: string, lines: ICartLineInput[]): Promise<TCart | null> => {
    const run = async (target: ICartLineInput[]) => {
      const data = await request<ICartLinesAddMutation>(cartLinesAddMutation, { cartId, lines: target });
      return data.cartLinesAdd;
    };

    const bulk = await run(lines);
    if (bulk?.cart) return bulk.cart;

    // 품절/판매중지 등 한 라인이 전체를 막지 않도록 개별 재시도. 전부 실패하면 옮길 수 없는 상품이므로 버림
    // 순차 실행: 앞 라인이 끝난 뒤 다음 라인을 추가하고, 성공한 마지막 카트를 이어받음 (예외가 나면 뒤 라인은 실행하지 않고 중단)
    return lines.reduce<Promise<TCart | null>>(async (previous, line) => {
      const latest = await previous;
      const result = await run([line]);
      return result?.cart ?? latest;
    }, Promise.resolve(null));
  };

  // 카트 라인 수량 변경(0이면 삭제). 실패하면 null을 반환해 호출부가 변경 전 카트를 기준으로 계속하게 함
  const updateLines = async (cartId: string, lines: ICartLineUpdateInput[]): Promise<TCart | null> => {
    const data = await request<ICartLinesUpdateMutation>(cartLinesUpdateMutation, { cartId, lines });
    return data.cartLinesUpdate?.cart ?? null;
  };

  // 카트에 로그인한 고객의 customerAccessToken 연결(소유권 설정)
  const bindBuyer = async (cartId: string, customerAccessToken: string): Promise<TCart> => {
    const data = await request<ICartBuyerIdentityUpdateMutation>(cartBuyerIdentityUpdateMutation, {
      cartId,
      buyerIdentity: { customerAccessToken },
    });

    // 무효 토큰이면 userErrors(INVALID)와 함께 buyer가 연결되지 않은 cart가 non-null로 옴 → 성공으로 취급하면 안 됨
    const result = data.cartBuyerIdentityUpdate;
    if (!result?.cart || result.userErrors.length) throw new Error("[CART SYNC] bindBuyer failed");
    return result.cart;
  };

  // 원격 카트가 없을 때, 구매자 연결 및 게스트 아이템을 한 번에 넣어 생성
  const createCart = async (lines: ICartLineInput[], customerAccessToken: string): Promise<TCart> => {
    const data = await request<ICartCreateInputMutation>(cartCreateInputMutation, {
      input: { lines, buyerIdentity: { customerAccessToken } },
    });

    const created = data.cartCreate.cart;
    if (!created || data.cartCreate.userErrors.length) throw new Error("[CART SYNC] cartCreate failed");
    return created;
  };

  /**
   * 같은 카트의 서버 값으로 스토어를 갱신. localStorage 스냅샷은 다른 기기에서 바뀐 내용을 모르기 때문에 필요함
   * 조회하는 사이 이 기기의 카트 mutation이 끝나 스토어가 더 최신이 됐다면 옛 응답으로 덮어쓰지 않도록 updatedAt으로 비교
   */
  const applyFresh = (fresh: TCart) => {
    const current = useCartStore.getState().cart;
    if (current.id === fresh.id && current.updatedAt && new Date(fresh.updatedAt) <= new Date(current.updatedAt)) return;
    updateCart(fresh);
  };

  /**
   * 원격 카트 조회, 만료됐거나(null) buyer가 정확히 본인이 아닌 카트는 없는 것으로 취급
   * 우리가 저장하는 카트는 항상 buyer가 연결돼 있으므로, buyer 없는 카트(=누군가 metafield에 심은 게스트 카트)도 거름
   */
  const fetchOwnRemoteCart = async (remoteId: string | null, id: string): Promise<TCart | null> => {
    const remote = remoteId ? await fetchCart(remoteId) : null;
    const owner = remote?.buyerIdentity?.customer?.id;
    return remote && owner && extractId(owner) === extractId(id) ? remote : null;
  };

  /**
   * 게스트 카트의 라인을 원격 카트에 합산
   * Product 구매 제한으로 충돌하면 나중에 담은 게스트 카트를 우선해 원격 카트의 같은 상품(다른 옵션)을 먼저 줄인 뒤 더함
   */
  const mergeGuestIntoRemote = async (local: TCart, remote: TCart, bound: TCart): Promise<TCart> => {
    const ordered = getCurrentOrderedQty();

    // 게스트 카트만 놓고 봤을 때 담을 수 있는 수량
    const guestLines = clampCarryLines(getCarryLines(local), local, null, ordered);

    const reductions = getRemoteReductions(guestLines, local, remote, ordered);
    const base = reductions.length ? ((await updateLines(remote.id, reductions)) ?? bound) : bound;

    const carryLines = clampCarryLines(guestLines, local, base, ordered);
    return carryLines.length ? ((await addLines(remote.id, carryLines)) ?? base) : base;
  };

  /**
   * 원격 카트 채택. buyer 연결을 먼저 함: 실패하면(토큰 만료 등) 원격 카트를 건드리기 전에 중단되므로,
   * 재시도 때 게스트 라인이 다시 더해져 수량이 두 배가 되지 않음
   */
  const adoptRemoteCart = async (local: TCart, ownership: TOwnership, remote: TCart, customerAccessToken: string) => {
    const bound = await bindBuyer(remote.id, customerAccessToken);
    return ownership === "guest" ? mergeGuestIntoRemote(local, remote, bound) : bound;
  };

  /** 원격 카트가 없을 때 새 카트 생성. 게스트 카트만 라인을 옮김 */
  const createOwnCart = (local: TCart, ownership: TOwnership, customerAccessToken: string) => {
    const isGuest = ownership === "guest";
    return createCart(isGuest ? clampCarryLines(getCarryLines(local), local, null, getCurrentOrderedQty()) : [], customerAccessToken);
  };

  /** 로컬 카트와 원격 카트를 동기화하고, 동기화 후 원격(metafield)에 저장돼 있는 cartId를 반환 */
  const runSync = async (id: string, customerAccessToken: string): Promise<string | null> => {
    const { cart: local } = useCartStore.getState();
    // 현재 브라우저(로컬) 스토어에 담긴 카트 정보 및 소유권 확인
    const ownership = getOwnership(local, id);

    // 고객 메타필드에 저장된 cartId를 가져와, 서버에 실제 존재하는 '내 카트'인지 확인
    const remote = await fetchOwnRemoteCart(await fetchRemoteCartId(customerAccessToken), id);

    // 원격 카트가 존재하고, 로컬 카트도 이미 'mine'이며, 두 카트 ID가 같은 경우
    if (remote && ownership === "mine" && remote.id === local.id) {
      assertStillLoggedIn(id, isJp); // 비동기 작업 도중 로그아웃했는지 체크
      applyFresh(remote); // updatedAt 비교 후 서버 최신 데이터로 스토어 갱신
      return remote.id;
    }

    // 원격 카트는 없는데, 로컬 카트가 이미 'mine'인 경우
    if (!remote && ownership === "mine") return null;

    // 원격 카트 유무에 따라 병합/채택 또는 새 카트 생성
    const next = remote
      ? await adoptRemoteCart(local, ownership, remote, customerAccessToken)
      : await createOwnCart(local, ownership, customerAccessToken);

    // 최종 동기화 완료 및 전역 스토어 업데이트
    assertStillLoggedIn(id, isJp);
    updateCart(next);

    return remote?.id ?? null;
  };

  // 로그인 상태 변경 또는 페이지 진입 시 실행
  useEffect(() => {
    // 비로그인 상태일 경우 상태 초기화
    if (!customerId || !hasSession) {
      lastSavedRef.current = null;
      setSyncedFor(null);
      return;
    }

    // cart.id 없음: 부팅 시 카트 생성 중. syncedFor: 이미 동기화 완료. runningRef: 중복 실행 방지(add 이중 실행 = 수량 두 배)
    if (!cart.id || syncedFor === customerId || runningRef.current) return;

    // "mine"이어도 다른 기기가 이미 원격 카트를 교체했을 수 있어(다른 id 채택 시 로컬 카트를 버림),
    // 시작 시점에는 병합/교체 여부를 알 수 없으므로 항상 막는다. 같은 id로 끝나는 빠른 경로에서도
    // updatedAt 비교(applyFresh)로 이중 보호되지만, 그 사이 구 카트id로 나간 뮤테이션 자체를 막기 위함
    runningRef.current = true;
    setSyncing(true);

    const id = customerId;

    // 로그인 감지 후 실행. jp는 buyerAccessToken을 비동기로 구해야 해서 먼저 resolve한 뒤 진입
    getBuyerAccessToken()
      .then((buyerAccessToken) => {
        if (!buyerAccessToken) throw new Error("[CART SYNC] no access token");
        return runSync(id, buyerAccessToken);
      })
      .then((remoteId) => {
        lastSavedRef.current = remoteId;
        setSyncedFor(id);
      })
      .catch((error) => {
        // 로컬 카트는 그대로 두므로 다음 부팅/카트 변경 시 재시도됨
        console.error("[CART SYNC] sync failed", error);
      })
      .finally(() => {
        runningRef.current = false;
        setSyncing(false);
      });
  }, [customerId, hasSession, cart.id, syncedFor]);

  // 동기화 이후 로그인한 카트의 id가 바뀌면 원격 cartId 갱신 (카트 생성, 만료 재생성, 주문 완료 후 빈 카트 재생성)
  useEffect(() => {
    if (!customerId || syncedFor !== customerId) return;
    if (!cart.id || cart.id === lastSavedRef.current) return;
    if (getOwnership(cart, customerId) !== "mine") return;

    const id = customerId;
    const cartId = cart.id;
    const previous = lastSavedRef.current;
    lastSavedRef.current = cartId;

    void getBuyerAccessToken().then((accessToken) => {
      if (!accessToken) {
        lastSavedRef.current = previous; // 토큰을 못 구하면 저장을 포기하고 다음 카트 변경 때 재시도
        return undefined;
      }

      return requestCartSync({ customerId: id, cartId, accessToken, domain: lang }).then((ok) => {
        // 실패하면 다음 카트 변경 때 다시 시도
        if (!ok && lastSavedRef.current === cartId) lastSavedRef.current = previous;
      });
    });
  }, [customerId, syncedFor, cart.id, cart.buyerIdentity?.customer?.id, lang]);
}
